/**
 * Maps routes to list CRUD workflows.
 * Staying inside a scope keeps remembered list filters; leaving clears them.
 * Filters also clear explicitly via "Show All" on the list page.
 */

const normalizePath = (pathname) =>
  String(pathname || "").replace(/\/$/, "") || "/";

const equalsOrChild = (base, path) => {
  const b = normalizePath(base);
  const p = normalizePath(path);
  return p === b || p.startsWith(`${b}/`);
};

const oneOf = (...predicates) => (path) => predicates.some((fn) => fn(path));

/**
 * @typedef {{ id: string, listPaths: string[], isInScope: (pathname: string) => boolean }} ListCrudScope
 */

/** @type {ListCrudScope[]} */
export const LIST_CRUD_SCOPES = [
  {
    id: "clients",
    listPaths: ["/clients"],
    isInScope: oneOf(
      (p) => equalsOrChild("/clients", p),
      (p) =>
        p === "/add_client" ||
        /^\/(edit_client|show_client)(\/|$)/.test(p)
    ),
  },
  {
    id: "trip-requests",
    listPaths: ["/trip_requests"],
    isInScope: (p) => equalsOrChild("/trip_requests", p),
  },
  {
    id: "transport-documents",
    listPaths: ["/transport_documents"],
    isInScope: (p) => equalsOrChild("/transport_documents", p),
  },
  {
    id: "client-offers",
    listPaths: ["/client_offers"],
    isInScope: (p) => equalsOrChild("/client_offers", p),
  },
  {
    // Related document tabs share one workflow so tab switches keep filters.
    id: "purchase-documents",
    listPaths: ["/purchase_invoices", "/debit-notes"],
    isInScope: oneOf(
      (p) => equalsOrChild("/purchase_invoices", p),
      (p) => equalsOrChild("/debit-notes", p)
    ),
  },
  {
    id: "sales-documents",
    listPaths: ["/sales-invoices", "/credit-notes"],
    isInScope: oneOf(
      (p) => equalsOrChild("/sales-invoices", p),
      (p) => equalsOrChild("/credit-notes", p)
    ),
  },
  {
    id: "bonds",
    listPaths: ["/bonds"],
    isInScope: (p) => equalsOrChild("/bonds", p),
  },
  {
    id: "drivers",
    listPaths: ["/drivers"],
    isInScope: oneOf(
      (p) => equalsOrChild("/drivers", p),
      (p) =>
        p === "/add_driver" ||
        /^\/(edit_driver|show_driver)(\/|$)/.test(p)
    ),
  },
  {
    id: "vehicles",
    listPaths: ["/vehicles_plural"],
    isInScope: (p) => equalsOrChild("/vehicles_plural", p),
  },
  {
    id: "tripway-partners",
    listPaths: ["/tripway_partners_list"],
    isInScope: oneOf(
      (p) => equalsOrChild("/tripway_partners_list", p),
      (p) =>
        p === "/add_tripway_partner" ||
        /^\/(edit_tripway_partner|show_tripway_partner)(\/|$)/.test(p)
    ),
  },
  {
    id: "users-winch-partners",
    listPaths: ["/users_winch_partners"],
    isInScope: (p) => equalsOrChild("/users_winch_partners", p),
  },
  {
    id: "partners-contracts",
    listPaths: ["/partners_contracts"],
    isInScope: oneOf(
      (p) => equalsOrChild("/partners_contracts", p),
      (p) => equalsOrChild("/winch-partner-contracts", p)
    ),
  },
  {
    id: "partners-docs",
    listPaths: ["/partners_docs"],
    isInScope: oneOf(
      (p) => equalsOrChild("/partners_docs", p),
      (p) => equalsOrChild("/winch-partner-documents", p)
    ),
  },
  {
    id: "business-sector",
    listPaths: ["/business_sector"],
    isInScope: (p) => {
      const path = normalizePath(p);
      if (path === "/business_sector") return true;
      if (!path.startsWith("/business_sector/")) return false;
      const rest = path.slice("/business_sector/".length);
      if (
        rest === "users" ||
        rest.startsWith("users/") ||
        rest === "contracts" ||
        rest.startsWith("contracts/") ||
        rest === "credit_limit" ||
        rest.startsWith("credit_limit/") ||
        rest === "business_docs" ||
        rest.startsWith("business_docs/") ||
        rest === "official_recieving_docs" ||
        rest.startsWith("official_recieving_docs/")
      ) {
        return false;
      }
      return /^(add|edit\/|view\/)/.test(rest);
    },
  },
  {
    id: "business-sector-users",
    listPaths: ["/business_sector/users"],
    isInScope: oneOf(
      (p) => equalsOrChild("/business_sector/users", p),
      (p) => equalsOrChild("/business-sector-users", p)
    ),
  },
  {
    id: "business-sector-contracts",
    listPaths: ["/business_sector/contracts"],
    isInScope: oneOf(
      (p) => equalsOrChild("/business_sector/contracts", p),
      (p) => equalsOrChild("/business-sector-contracts", p)
    ),
  },
  {
    id: "business-sector-credit-limit",
    listPaths: ["/business_sector/credit_limit"],
    isInScope: (p) => equalsOrChild("/business_sector/credit_limit", p),
  },
  {
    id: "business-sector-docs",
    listPaths: ["/business_sector/business_docs"],
    isInScope: oneOf(
      (p) => equalsOrChild("/business_sector/business_docs", p),
      (p) => equalsOrChild("/business-sector-documents", p)
    ),
  },
  {
    id: "business-sector-official-docs",
    listPaths: ["/business_sector/official_recieving_docs"],
    isInScope: oneOf(
      (p) => equalsOrChild("/business_sector/official_recieving_docs", p),
      (p) => equalsOrChild("/business-sector-responsibles", p)
    ),
  },
  {
    id: "account-statement",
    listPaths: ["/account_statement"],
    isInScope: oneOf(
      (p) => equalsOrChild("/account_statement", p),
      // Linked document details opened from the report reference column
      (p) => /^\/sales-invoices\/show(\/|$)/.test(normalizePath(p)),
      (p) => /^\/purchase_invoices\/show(\/|$)/.test(normalizePath(p)),
      (p) => /^\/credit-notes\/show(\/|$)/.test(normalizePath(p)),
      (p) => /^\/debit-notes\/show(\/|$)/.test(normalizePath(p)),
      (p) => /^\/bonds\/show(\/|$)/.test(normalizePath(p)),
      (p) => /^\/manual_entries\/view(\/|$)/.test(normalizePath(p))
    ),
  },
  {
    id: "general-ledger",
    listPaths: ["/general_ledger"],
    isInScope: oneOf(
      (p) => equalsOrChild("/general_ledger", p),
      (p) => /^\/show_chart_accounts(\/|$)/.test(normalizePath(p))
    ),
  },
  {
    id: "journal-book",
    listPaths: ["/journal_book"],
    isInScope: oneOf(
      (p) => equalsOrChild("/journal_book", p),
      (p) => /^\/sales-invoices\/show(\/|$)/.test(normalizePath(p)),
      (p) => /^\/purchase_invoices\/show(\/|$)/.test(normalizePath(p)),
      (p) => /^\/credit-notes\/show(\/|$)/.test(normalizePath(p)),
      (p) => /^\/debit-notes\/show(\/|$)/.test(normalizePath(p)),
      (p) => /^\/bonds\/show(\/|$)/.test(normalizePath(p)),
      (p) => /^\/manual_entries\/view(\/|$)/.test(normalizePath(p)),
      (p) => /^\/show_chart_accounts(\/|$)/.test(normalizePath(p)),
      (p) => /^\/business_sector\/view(\/|$)/.test(normalizePath(p)),
      (p) => /^\/show_driver(\/|$)/.test(normalizePath(p))
    ),
  },
  {
    id: "trial-balance",
    listPaths: ["/trial_balance"],
    isInScope: oneOf(
      (p) => equalsOrChild("/trial_balance", p),
      (p) => /^\/show_chart_accounts(\/|$)/.test(normalizePath(p))
    ),
  },
  {
    id: "customer-dues",
    listPaths: ["/customer_dues"],
    isInScope: oneOf(
      (p) => equalsOrChild("/customer_dues", p),
      (p) => /^\/business_sector\/view(\/|$)/.test(normalizePath(p)),
      (p) => /^\/show_driver(\/|$)/.test(normalizePath(p)),
      (p) => /^\/show_tripway_partner(\/|$)/.test(normalizePath(p)),
      (p) => /^\/clients\/show_client(\/|$)/.test(normalizePath(p))
    ),
  },
  {
    id: "income-statement",
    listPaths: ["/income_statement"],
    isInScope: oneOf(
      (p) => equalsOrChild("/income_statement", p),
      (p) => /^\/show_chart_accounts(\/|$)/.test(normalizePath(p))
    ),
  },
  {
    id: "balance-sheet",
    listPaths: ["/balance_sheet"],
    isInScope: oneOf(
      (p) => equalsOrChild("/balance_sheet", p),
      (p) => /^\/show_chart_accounts(\/|$)/.test(normalizePath(p))
    ),
  },
  {
    id: "tax-invoice-reports",
    listPaths: ["/tax_invoice_reports"],
    isInScope: oneOf(
      (p) => equalsOrChild("/tax_invoice_reports", p),
      (p) => /^\/sales-invoices\/show(\/|$)/.test(normalizePath(p)),
      (p) => /^\/purchase_invoices\/show(\/|$)/.test(normalizePath(p)),
      (p) => /^\/credit-notes\/show(\/|$)/.test(normalizePath(p)),
      (p) => /^\/debit-notes\/show(\/|$)/.test(normalizePath(p)),
      (p) => /^\/business_sector\/view(\/|$)/.test(normalizePath(p)),
      (p) => /^\/show_driver(\/|$)/.test(normalizePath(p)),
      (p) => /^\/show_tripway_partner(\/|$)/.test(normalizePath(p)),
      (p) => /^\/clients\/show_client(\/|$)/.test(normalizePath(p))
    ),
  },
  {
    id: "customer-profit",
    listPaths: ["/customer_profit"],
    isInScope: oneOf(
      (p) => equalsOrChild("/customer_profit", p),
      (p) => /^\/business_sector\/view(\/|$)/.test(normalizePath(p))
    ),
  },
  {
    id: "manual-entries",
    listPaths: ["/manual_entries"],
    isInScope: (p) => equalsOrChild("/manual_entries", p),
  },
  {
    id: "chart-of-accounts",
    listPaths: ["/chart_of_accounts"],
    isInScope: (p) => equalsOrChild("/chart_of_accounts", p),
  },
  {
    id: "cost-center-accounts",
    listPaths: ["/cost_center_accounts"],
    isInScope: (p) => equalsOrChild("/cost_center_accounts", p),
  },
  {
    id: "financial-years",
    listPaths: ["/financial_years"],
    isInScope: (p) => equalsOrChild("/financial_years", p),
  },
  {
    id: "quotations-requests",
    listPaths: ["/quotations_requests"],
    isInScope: (p) => equalsOrChild("/quotations_requests", p),
  },
  {
    id: "quotations-requests-suppliers",
    listPaths: ["/quotations_requests_suppliers"],
    isInScope: (p) => equalsOrChild("/quotations_requests_suppliers", p),
  },
  {
    id: "export-orders",
    listPaths: ["/export_orders"],
    isInScope: (p) => equalsOrChild("/export_orders", p),
  },
];

export const findListCrudScope = (pathname) => {
  const path = normalizePath(pathname);
  return LIST_CRUD_SCOPES.find((scope) => scope.isInScope(path)) || null;
};
